import React, { useEffect, useState } from 'react';
import { Col, Row, Container, ListGroup, Tab, Nav, Accordion } from "react-bootstrap";
import Main from "../Main";
import customerImg from '../../assets/images/CustomerManagement/customer-img.svg';
import Download from '../MainSection/Download/Download';
import { useNavigate } from 'react-router-dom';
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import * as XLSX from 'xlsx';
import jsPDF from "jspdf";
import "jspdf-autotable";

const CustomerViewDetails = ({ handleAnalyticsClick, singleCustomerData, setShowCustomerViewDetails, dataLoading }) => {

    // State to manage which accordion item is open
    const [activeKey, setActiveKey] = useState(null);

    // Separate state for each tab's active accordion item
    const [activeBusinessUnitKey, setActiveBusinessUnitKey] = useState(null);
    const [activeBranchPlantKey, setActiveBranchPlantKey] = useState(null);
    const [activeStorageLocationKey, setActiveStorageLocationKey] = useState(null);

    const handleBack = () => {
        setShowCustomerViewDetails(false);
    }

    // Extract data from the API response
    // Safely access customer data, and fallback to empty if undefined or empty
    const customers = singleCustomerData?.Customers || [];
    const customer = customers[0]; // Safely get the first customer or undefined if empty

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const businessUnits = singleCustomerData.BusinessUnits || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const branchPlants = singleCustomerData.Branch || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const storageLocations = singleCustomerData.Slocs || [];

    // Set the default active key for each accordion when data is loaded
    useEffect(() => {
        if (businessUnits.length > 0) {
            setActiveBusinessUnitKey(businessUnits[0].BusinessUnitID.toString()); // Set first business unit open by default
        }
        if (branchPlants.length > 0) {
            setActiveBranchPlantKey(branchPlants[0].BranchID.toString()); // Set first branch plant open by default
        }
        if (storageLocations.length > 0) {
            setActiveStorageLocationKey(storageLocations[0].SlockID.toString()); // Set first storage location open by default
        }
    }, [businessUnits, branchPlants, storageLocations]);  // Only run this when data is loaded

    const handleDownloadExcel = () => {
        if (!singleCustomerData) return;

        const customers = singleCustomerData?.Customers || [];
        const businessUnits = singleCustomerData?.BusinessUnits || [];
        const branchPlants = singleCustomerData?.Branch || [];
        const storageLocations = singleCustomerData?.Slocs || [];

        const companySheetData = customers.map(item => ({
            "ID": item.CompanyID || "NA",
            "COMPANY CODE": item.CompanyCode || "NA",
            "COMPANY": item.CompanyName || "NA",
            "CONTACT NAME": item.ContactPerson || "NA",
            "NO. OF LICENSES": item.NoOfLicenses || "NA",
            "EMAIL": item.EmailID || "NA",
            "ADDRESS": item.Address || "NA",
            "ASSIGNED MODULES": item.AssignedModuleNames || "NA",
            "START DATE": item.StartDate || "NA",
            "END DATE": item.EndDate || "NA",
            "STATUS": item.Status || "NA",
        }));

        const businessUnitSheetData = businessUnits.map(unit => ({
            "BUSINESS UNIT ID": unit.BusinessUnitID || "NA",
            "UNIT CODE": unit.UnitCode || "NA",
            "UNIT NAME": unit.UnitName || "NA",
            "REPORTING TO": unit.ReportingTo || "NA",
        }));

        const branchPlantSheetData = branchPlants.map(branch => ({
            "BRANCH ID": branch.BranchID || "NA",
            "PLANT CODE": branch.BranchCode || "NA",
            "PLANT NAME": branch.BranchName || "NA",
            "EMAIL": branch.EmailID || "NA",
            "CONTACT NUMBER": branch.Phone || "NA",
            "REPORTING TYPE": branch.ReportingType || "NA",
            "REPORTING TO": branch.ReportingTo || "NA",
            "NO. OF LICENSES": branch.NoOfLicenses || "NA",
            "ADDRESS": branch.Address || "NA",
            "ZIP CODE": branch.ZipCode || "NA",
        }));

        const storageLocationSheetData = storageLocations.map(loc => ({
            "SLOC ID": loc.SlockID || "NA",
            "SLOC CODE": loc.StorageLocationCode || "NA",
            "SLOC NAME": loc.StorageLocationName || "NA",
            "REPORTING TYPE": loc.ReportingType || "NA",
            "REPORTING TO": loc.ReportingTo || "NA",
        }));

        // Create workbook and append sheets
        const wb = XLSX.utils.book_new();

        if (companySheetData.length > 0) {
            const wsCompany = XLSX.utils.json_to_sheet(companySheetData);
            XLSX.utils.book_append_sheet(wb, wsCompany, "Company");
        }

        if (businessUnitSheetData.length > 0) {
            const wsBU = XLSX.utils.json_to_sheet(businessUnitSheetData);
            XLSX.utils.book_append_sheet(wb, wsBU, "Business Unit");
        }

        if (branchPlantSheetData.length > 0) {
            const wsBranch = XLSX.utils.json_to_sheet(branchPlantSheetData);
            XLSX.utils.book_append_sheet(wb, wsBranch, "Plant");
        }

        if (storageLocationSheetData.length > 0) {
            const wsSLOC = XLSX.utils.json_to_sheet(storageLocationSheetData);
            XLSX.utils.book_append_sheet(wb, wsSLOC, "Storage Location");
        }

        // Trigger download
        XLSX.writeFile(wb, 'customer_details.xlsx');
    };

    const handleDownloadPDF = () => {
        const doc = new jsPDF({ orientation: "landscape" });
        let startY = 20;

        // ---------------------- COMPANY SHEET ----------------------
        doc.setFontSize(18);
        doc.text("Company Details", 14, startY);
        startY += 6;

        const companyDataToExport = singleCustomerData?.Customers?.map(item => ([
            item.CompanyID || "NA",
            item.CompanyCode || "NA",
            item.CompanyName || "NA",
            item.ContactPerson || "NA",
            item.NoOfLicenses || "NA",
            item.EmailID || "NA",
            item.Address || "NA",
            item.StartDate || "NA",
            item.EndDate || "NA",
            item.Status || "NA",
            item.AssignedModuleNames || "NA",
        ])) || [];

        doc.autoTable({
            head: [[
                "ID",
                "COMPANY CODE",
                "COMPANY",
                "CONTACT NAME",
                "NO. OF LICENSES",
                "EMAIL",
                "ADDRESS",
                "START DATE",
                "END DATE",
                "STATUS",
                "ASSIGNED MODULES"
            ]],
            body: companyDataToExport,
            startY: startY,
            styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
            headStyles: { fillColor: [22, 160, 133], textColor: 255 },
        });

        startY = doc.lastAutoTable.finalY + 10;

        // ---------------------- BUSINESS UNIT ----------------------
        doc.setFontSize(18);
        doc.text("Business Unit Details", 14, startY);
        startY += 6;

        const businessUnitData = singleCustomerData?.BusinessUnits?.map(unit => ([
            unit.BusinessUnitID || "NA",
            unit.UnitCode || "NA",
            unit.UnitName || "NA",
            unit.ReportingTo || "NA",
        ])) || [];

        doc.autoTable({
            head: [["BUSINESS UNIT ID", "UNIT CODE", "UNIT NAME", "REPORTING TO"]],
            body: businessUnitData,
            startY: startY,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [52, 152, 219], textColor: 255 },
        });

        startY = doc.lastAutoTable.finalY + 10;

        // ---------------------- PLANT / BRANCH ----------------------
        doc.setFontSize(18);
        doc.text("Plant Details", 14, startY);
        startY += 6;

        const branchData = singleCustomerData?.Branch?.map(branch => ([
            branch.BranchID || "NA",
            branch.BranchCode || "NA",
            branch.BranchName || "NA",
            branch.EmailID || "NA",
            branch.Phone || "NA",
            branch.ReportingType || "NA",
            branch.ReportingTo || "NA",
            branch.NoOfLicenses || "NA",
            branch.Address || "NA",
            branch.ZipCode || "NA",
        ])) || [];

        doc.autoTable({
            head: [[
                "BRANCH ID",
                "PLANT CODE",
                "PLANT NAME",
                "EMAIL",
                "CONTACT NUMBER",
                "REPORTING TYPE",
                "REPORTING TO",
                "NO. OF LICENSES",
                "ADDRESS",
                "ZIP CODE"
            ]],
            body: branchData,
            startY: startY,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [231, 76, 60], textColor: 255 },
        });

        startY = doc.lastAutoTable.finalY + 10;

        // ---------------------- STORAGE LOCATION ----------------------
        doc.setFontSize(18);
        doc.text("Storage Location Details", 14, startY);
        startY += 6;

        const slocData = singleCustomerData?.Slocs?.map(loc => ([
            loc.SlockID || "NA",
            loc.StorageLocationCode || "NA",
            loc.StorageLocationName || "NA",
            loc.ReportingType || "NA",
            loc.ReportingTo || "NA",
        ])) || [];

        doc.autoTable({
            head: [["SLOC ID", "SLOC CODE", "SLOC NAME", "REPORTING TYPE", "REPORTING TO"]],
            body: slocData,
            startY: startY,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [155, 89, 182], textColor: 255 },
        });

        doc.save("customer_management.pdf");
    };


    return (
        <div className="customer-management-view-details-container">
            <Main>
                {/* <Container> */}
                <div className='components-download-container'>
                    <Download
                        moduleName="Back"
                        showBackButton={true}
                        showDashboardLibraryButton={false}
                        showSaveCustomViewButton={false}
                        showAnalyticsButton={true}
                        showDownloadDropdown={true}
                        onClick={handleAnalyticsClick}
                        onBackClick={handleBack}
                        onExcelDownloadClick={handleDownloadExcel}
                        onPdfDownloadClick={handleDownloadPDF}
                    />
                </div>
                <div className='customermanagement-content-container'>
                    <div>
                        <Row className="customermanagement-view-details-btns-container">
                            {/* Left Side - Head-Quarters Details */}
                            <Col md={4} className='customermanagement-view-details-first-col'>
                                <div className="customermanagement-view-details-header">
                                    <p>Head Quarters Details</p>
                                    {/* <img src={customerImg} alt="" /> */}
                                    {dataLoading ? (
                                        <Skeleton height={50} />
                                    ) : (
                                        <img src={
                                            customer?.Image
                                                ? customer.Image.startsWith('data:image/')  // Check if it's base64 with MIME type
                                                    ? customer.Image  // Use as is if it's base64 with MIME type
                                                    : customer.Image.includes('http')  // Check if it's a URL
                                                        ? customer.Image  // Use directly if it's a server URL
                                                        : `data:image/;base64,${customer.Image}`  // If base64 without MIME, prepend MIME type
                                                : customerImg  // Use fallback image if no image exists
                                        }
                                            alt=""
                                            style={{ width: "100px", height: "100px" }}
                                        />)}
                                </div>
                                <ListGroup variant="flush" className='head-quarters-list-items'>
                                    <ListGroup.Item className='mt-3'><p>Company Code</p> <p>:</p>
                                        {dataLoading ? (
                                            <Skeleton height={28} />
                                        ) : (<p>{customer?.CompanyCode}</p>)}
                                    </ListGroup.Item>
                                    <ListGroup.Item><p>Company Name</p> <p>: </p>
                                        {dataLoading ? (
                                            <Skeleton height={28} />
                                        ) : (<p>{customer?.CompanyName}</p>)}
                                    </ListGroup.Item>
                                    <ListGroup.Item><p>Contact Name</p> <p>:</p>
                                        {dataLoading ? (
                                            <Skeleton height={28} />
                                        ) : (<p>{customer?.ContactPerson}</p>)}
                                    </ListGroup.Item>
                                    <ListGroup.Item><p>Email ID</p><p>:</p>
                                        {dataLoading ? (
                                            <Skeleton height={28} />
                                        ) : (<p>{customer?.EmailID}</p>)}
                                    </ListGroup.Item>
                                    <ListGroup.Item><p>No. of Licenses</p> <p>:</p>
                                        {dataLoading ? (
                                            <Skeleton height={28} />
                                        ) : (<p>{customer?.NoOfLicenses}</p>)}
                                    </ListGroup.Item>
                                    <ListGroup.Item><p>Contact Number</p> <p>:</p>
                                        {dataLoading ? (
                                            <Skeleton height={28} />
                                        ) : (<p>{customer?.Mobile}</p>)}
                                    </ListGroup.Item>
                                    <ListGroup.Item><p>Classification</p> <p>:</p>
                                        {dataLoading ? (
                                            <Skeleton height={28} />
                                        ) : (<p>{customer?.Classification}</p>)}
                                    </ListGroup.Item>
                                    <ListGroup.Item><p>Address</p> <p>:</p>
                                        {dataLoading ? (
                                            <Skeleton height={28} />
                                        ) : (<p>{customer?.Address}</p>)}
                                    </ListGroup.Item>
                                    <ListGroup.Item><p>Zip Code</p> <p>:</p>
                                        {dataLoading ? (
                                            <Skeleton height={28} />
                                        ) : (<p>{customer?.ZipCode}</p>)}
                                    </ListGroup.Item>
                                    {/* <ListGroup.Item><p>Relationship Type</p> <p>:</p><p>{customer.CompanyCode}</p></ListGroup.Item> */}
                                    <ListGroup.Item><p>Start Date</p> <p>:</p>
                                        {dataLoading ? (
                                            <Skeleton height={28} />
                                        ) : (<p>{customer?.StartDate}</p>)}
                                    </ListGroup.Item>
                                    <ListGroup.Item><p>End Date</p> <p>:</p>
                                        {dataLoading ? (
                                            <Skeleton height={28} />
                                        ) : (<p>{customer?.EndDate}</p>)}
                                    </ListGroup.Item>
                                    {/* <ListGroup.Item><p>Email ID</p> <p>:</p><p>{customer.CompanyCode}</p></ListGroup.Item> */}
                                    <ListGroup.Item><p>Assigned Modules</p><p>:</p>
                                        {dataLoading ? (
                                            <Skeleton height={28} />
                                        ) : (<p>{customer?.AssignedModuleNames}</p>)}
                                    </ListGroup.Item>
                                    <ListGroup.Item><p>Website</p> <p>:</p>
                                        {dataLoading ? (
                                            <Skeleton height={28} />
                                        ) : (<p>{customer?.Website}</p>)}
                                    </ListGroup.Item>
                                    <ListGroup.Item><p>Status</p><p>:</p>
                                        {dataLoading ? (
                                            <Skeleton height={28} />
                                        ) : (<p className="text-success-color">{customer?.Status}</p>)}
                                    </ListGroup.Item>
                                </ListGroup>
                            </Col>

                            {/* Right Side - Dynamic Content */}
                            <Col md={8}>
                                <div className="customermanagement-view-details-second-header">
                                    <p>Plant Variant Details</p>
                                </div>
                                <Tab.Container defaultActiveKey="businessUnit">
                                    <Nav variant="tabs">
                                        <Nav.Item>
                                            <Nav.Link eventKey="businessUnit">Business Unit ({businessUnits.length})</Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link eventKey="branchPlant"> Plant ({branchPlants.length})</Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link eventKey="storageLocation">Storage Location ({storageLocations.length})</Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                    <Tab.Content className='customermanagement-tab-accordion'>
                                        <Tab.Pane eventKey="businessUnit">
                                            <Accordion activeKey={activeBusinessUnitKey} onSelect={(key) => setActiveBusinessUnitKey(key)}>
                                                {businessUnits.map((unit, index) => (
                                                    <Accordion.Item eventKey={unit.BusinessUnitID.toString()} key={unit.BusinessUnitID}>
                                                        <Accordion.Header className='customermanagement-view-accordian-header'>Business Unit {index + 1}</Accordion.Header>
                                                        <Accordion.Body className='customermanagement-accordian-body'>
                                                            <Row>
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Business Unit Code</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{unit.UnitCode}</span>)}
                                                                </Col>
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Business Unit Name</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{unit.UnitName}</span>)}
                                                                </Col>
                                                                {/* <Col md={3} className='mb-3'>
                                                                    <p>Relationship Type</p>
                                                                    <span>{unit.ReportingType}</span>
                                                                </Col> */}
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Reporting To</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{unit.ReportingTo}</span>)}
                                                                </Col>
                                                            </Row>
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                ))}
                                            </Accordion>
                                        </Tab.Pane>
                                        <Tab.Pane eventKey="branchPlant">
                                            <Accordion activeKey={activeBranchPlantKey} onSelect={(key) => setActiveBranchPlantKey(key)}>
                                                {branchPlants.map((branch, index) => (
                                                    <Accordion.Item eventKey={branch.BranchID.toString()} key={branch.BranchID}>
                                                        <Accordion.Header className='customermanagement-view-accordian-header'> Plant {index + 1}</Accordion.Header>
                                                        <Accordion.Body className='customermanagement-accordian-body'>
                                                            <Row>
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Plant Code</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{branch.BranchCode}</span>)}
                                                                </Col>
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Plant Name</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{branch.BranchName}</span>)}
                                                                </Col>
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Email</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{branch.EmailID}</span>)}
                                                                </Col>

                                                            </Row>
                                                            <Row>
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Contact number</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{branch.Phone}</span>)}
                                                                </Col>
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Reporting Type</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{branch.ReportingType}</span>)}
                                                                </Col>
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Reporting To</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{branch.ReportingTo || "N/A"}</span>)}
                                                                </Col>
                                                            </Row>
                                                            <Row>
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Number Of Licenses</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{branch.NoOfLicenses}</span>)}
                                                                </Col>
                                                                {/* <Col md={4} className='mb-3'>
                                                                    <p>Relationship Type</p>
                                                                    <span>{branch.details.relationship}</span>
                                                                </Col> */}
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Address</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{branch.Address}</span>)}
                                                                </Col>
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Zipcode</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{branch.ZipCode}</span>)}
                                                                </Col>
                                                            </Row>
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                ))}
                                            </Accordion>
                                        </Tab.Pane>
                                        <Tab.Pane eventKey="storageLocation">
                                            <Accordion activeKey={activeStorageLocationKey} onSelect={(key) => setActiveStorageLocationKey(key)}>
                                                {storageLocations.map((location, index) => (
                                                    <Accordion.Item eventKey={location.SlockID.toString()} key={location.SlockID}>
                                                        <Accordion.Header className='customermanagement-view-accordian-header'> Storage Location {index + 1}</Accordion.Header>
                                                        <Accordion.Body className='customermanagement-accordian-body'>
                                                            <Row>
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Storage Location Code</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{location.StorageLocationCode}</span>)}
                                                                </Col>
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Storage Location Name</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{location.StorageLocationName}</span>)}
                                                                </Col>
                                                                {/* <Col md={4} className='mb-3'>
                                                                    <p>Relationship Type</p>
                                                                    <span>{location.details.type}</span>
                                                                </Col> */}
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Reporting Type</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{location.ReportingType}</span>)}
                                                                </Col>
                                                            </Row>
                                                            <Row>
                                                                <Col md={4} className='mb-3'>
                                                                    <p>Reporting To</p>
                                                                    {dataLoading ? (
                                                                        <Skeleton height={28} />
                                                                    ) : (<span>{location.ReportingTo}</span>)}
                                                                </Col>
                                                            </Row>
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                ))}
                                            </Accordion>
                                        </Tab.Pane>
                                    </Tab.Content>
                                </Tab.Container>
                            </Col>
                        </Row>
                    </div>
                </div>
                {/* </Container> */}
            </Main >
        </div >
    );
}

export default CustomerViewDetails;

